import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Sparkles, ArrowUpRight, Star, Check, Mail, Phone } from "lucide-react";

export default function SiteView() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/site/${slug}`).then((r) => setData(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-[#05050A] text-white grid place-items-center">
        <div className="glass rounded-2xl p-10 max-w-md text-center">
          <h1 className="font-heading text-3xl font-black">Site not found</h1>
          <Link to="/" className="mt-5 inline-flex brand-gradient-text font-semibold">
            Go home →
          </Link>
        </div>
      </div>
    );
  }
  if (!data) return <div className="min-h-screen bg-[#05050A] grid place-items-center text-white/40 font-mono">Loading…</div>;

  const s = data.site;
  const brandColor = data.brand_color || "#F97316";

  return (
    <div className="min-h-screen bg-[#05050A] text-white" data-testid="site-view-root" style={{ "--brand": brandColor }}>
      {/* Nav */}
      <header className="sticky top-0 z-30 glass-strong border-b border-white/5">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div
              className="h-8 w-8 rounded-lg grid place-items-center font-heading font-black"
              style={{ background: brandColor }}
            >
              {data.business_name?.[0]}
            </div>
            <span className="font-heading font-bold tracking-tight">{data.business_name}</span>
          </div>
          <a
            href="#contact"
            className="px-4 py-2 rounded-lg text-sm font-semibold hover:opacity-90"
            style={{ background: brandColor }}
          >
            {s.hero?.primary_cta || "Get in touch"}
          </a>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div
          className="absolute -top-20 -right-20 h-96 w-96 opacity-30 blur-3xl rounded-full"
          style={{ background: brandColor }}
        />
        <div className="max-w-6xl mx-auto px-6 py-24 lg:py-32 relative">
          {s.hero?.eyebrow && (
            <div className="text-xs uppercase tracking-[0.25em] mb-5" style={{ color: brandColor }}>
              {s.hero.eyebrow}
            </div>
          )}
          <h1 className="font-heading text-5xl lg:text-7xl font-black tracking-tighter leading-[0.95] max-w-4xl">
            {s.hero?.headline}
          </h1>
          <p className="mt-6 text-lg text-white/70 max-w-2xl">{s.hero?.subheadline}</p>
          <div className="mt-10 flex flex-wrap gap-3">
            <a
              href="#contact"
              className="px-6 py-3.5 rounded-lg font-semibold text-sm text-white hover:opacity-90"
              style={{ background: brandColor }}
            >
              {s.hero?.primary_cta} <ArrowUpRight className="h-4 w-4 inline ml-1" />
            </a>
            <a href="#services" className="glass px-6 py-3.5 rounded-lg font-semibold text-sm hover:bg-white/5">
              {s.hero?.secondary_cta}
            </a>
          </div>
        </div>
      </section>

      {/* Features */}
      {Array.isArray(s.features) && (
        <section className="max-w-6xl mx-auto px-6 py-20">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {s.features.map((f, i) => (
              <div key={i} className="glass rounded-2xl p-6">
                <div className="h-10 w-10 rounded-lg grid place-items-center mb-4 text-xl" style={{ background: brandColor }}>
                  <Check className="h-5 w-5" />
                </div>
                <h3 className="font-heading font-bold text-lg">{f.title}</h3>
                <p className="text-sm text-white/60 mt-2 leading-relaxed">{f.description}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* About */}
      {s.about && (
        <section className="max-w-6xl mx-auto px-6 py-20">
          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <h2 className="font-heading text-4xl lg:text-5xl font-black tracking-tight">{s.about.title}</h2>
              <p className="text-white/70 mt-6 leading-relaxed">{s.about.story}</p>
            </div>
            <div className="grid grid-cols-3 gap-4">
              {(s.about.stats || []).map((st, i) => (
                <div key={i} className="glass rounded-2xl p-6 text-center">
                  <div className="font-heading text-3xl font-black" style={{ color: brandColor }}>{st.value}</div>
                  <div className="text-xs text-white/50 mt-1 uppercase tracking-[0.2em]">{st.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Services */}
      {Array.isArray(s.services) && (
        <section id="services" className="max-w-6xl mx-auto px-6 py-20">
          <h2 className="font-heading text-4xl font-black tracking-tight mb-10">Services</h2>
          <div className="grid md:grid-cols-3 gap-5">
            {s.services.map((sv, i) => (
              <div key={i} className="glass rounded-2xl p-6 hover:bg-white/[0.04] transition">
                <h3 className="font-heading font-bold text-xl">{sv.name}</h3>
                <p className="text-sm text-white/60 mt-3 leading-relaxed">{sv.description}</p>
                <div className="mt-5 text-xs uppercase tracking-[0.2em] font-mono" style={{ color: brandColor }}>
                  {sv.price_hint}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Testimonials */}
      {Array.isArray(s.testimonials) && (
        <section className="max-w-6xl mx-auto px-6 py-20">
          <h2 className="font-heading text-4xl font-black tracking-tight mb-10">What people say</h2>
          <div className="grid md:grid-cols-3 gap-5">
            {s.testimonials.map((t, i) => (
              <div key={i} className="glass rounded-2xl p-6">
                <div className="flex gap-1 mb-4">
                  {Array.from({ length: 5 }).map((_, k) => (
                    <Star key={k} className="h-4 w-4 fill-current" style={{ color: brandColor }} />
                  ))}
                </div>
                <p className="text-white/80">"{t.quote}"</p>
                <div className="mt-4">
                  <div className="font-semibold">{t.author}</div>
                  <div className="text-xs text-white/50">{t.role}</div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* FAQ */}
      {Array.isArray(s.faq) && (
        <section className="max-w-3xl mx-auto px-6 py-20">
          <h2 className="font-heading text-4xl font-black tracking-tight mb-10 text-center">FAQs</h2>
          <div className="space-y-3">
            {s.faq.map((q, i) => (
              <details key={i} className="glass rounded-2xl p-5 group">
                <summary className="cursor-pointer font-semibold list-none flex items-center justify-between">
                  {q.question}
                  <span className="text-white/40 group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="text-white/70 mt-3 leading-relaxed text-sm">{q.answer}</p>
              </details>
            ))}
          </div>
        </section>
      )}

      {/* CTA */}
      {s.cta_section && (
        <section className="max-w-5xl mx-auto px-6 py-16">
          <div className="glass rounded-3xl p-10 lg:p-16 relative overflow-hidden">
            <div className="absolute -top-20 -right-20 h-80 w-80 opacity-20 blur-3xl rounded-full" style={{ background: brandColor }} />
            <div className="relative">
              <h2 className="font-heading text-3xl lg:text-5xl font-black tracking-tight max-w-2xl">
                {s.cta_section.headline}
              </h2>
              <p className="text-white/70 mt-4 max-w-xl">{s.cta_section.subheadline}</p>
              <a
                href="#contact"
                className="mt-8 inline-flex items-center gap-2 px-6 py-3.5 rounded-lg font-semibold text-sm hover:opacity-90"
                style={{ background: brandColor }}
              >
                {s.cta_section.button} <ArrowUpRight className="h-4 w-4" />
              </a>
            </div>
          </div>
        </section>
      )}

      {/* Contact */}
      {s.contact && (
        <section id="contact" className="max-w-3xl mx-auto px-6 py-20 text-center">
          <h2 className="font-heading text-4xl font-black tracking-tight">{s.contact.headline}</h2>
          <p className="text-white/60 mt-4">{s.contact.subheadline}</p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            {s.contact.email && (
              <a href={`mailto:${s.contact.email}`} className="glass px-5 py-3 rounded-lg text-sm inline-flex items-center gap-2">
                <Mail className="h-4 w-4" /> {s.contact.email}
              </a>
            )}
            {s.contact.phone && (
              <a href={`tel:${s.contact.phone}`} className="glass px-5 py-3 rounded-lg text-sm inline-flex items-center gap-2">
                <Phone className="h-4 w-4" /> {s.contact.phone}
              </a>
            )}
          </div>
        </section>
      )}

      <footer className="border-t border-white/5">
        <div className="max-w-6xl mx-auto px-6 py-6 text-xs text-white/40 font-mono flex flex-wrap justify-between gap-3">
          <span>© 2026 {data.business_name}</span>
          <Link to="/" className="hover:text-white">
            <span className="flex items-center gap-1.5">
              <Sparkles className="h-3 w-3" /> Built with MakeYourBrand.IN
            </span>
          </Link>
        </div>
      </footer>
    </div>
  );
}
